import { useEffect, useState, useCallback, Fragment } from "react";
import { Plus, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, MobileRow } from "@/components/Shared";
import { ShippingBadge, ShippingTimeline, ShippingDialog } from "@/components/Shipping";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUSES = ["pending", "approved", "ordered", "delivered", "rejected"];
const EMPTY = {
  item_name: "", quantity: 1, unit: "pcs", priority: "normal",
  needed_by: "", vendor_name: "", notes: "",
};

export default function Requests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [shipTarget, setShipTarget] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const canManage = user?.role === "admin" || user?.role === "vendor";

  const load = useCallback(async () => {
    const { data } = await api.get("/requests", { params: { status: tab } });
    setRows(data);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/requests", { ...form, quantity: Number(form.quantity) });
      toast.success("Supply request submitted");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/requests/${id}/status`, { status });
      toast.success(
        status === "delivered" ? "Delivered — stock added to inventory" : `Marked ${status}`
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="requests-page">
      <PageHeader
        overline="Procurement"
        title="Supply Requests"
        description="Raise what the floor needs; vendors and admins move it through to delivery — delivered items are added to inventory automatically."
        action={
          <Button data-testid="add-request-button" onClick={() => setOpen(true)} className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
            <Plus size={16} className="mr-2" /> New request
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger data-testid="request-filter-all" value="all" className="rounded-none text-xs uppercase tracking-wider">All</TabsTrigger>
          {STATUSES.map((s) => (
            <TabsTrigger key={s} data-testid={`request-filter-${s}`} value={s} className="rounded-none text-xs uppercase tracking-wider">{s}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {rows.length === 0 ? (
        <EmptyState testid="requests-empty" title="No supply requests" description="Create a request for materials, consumables or equipment." />
      ) : (
        <>
          <div className="border border-border bg-white md:hidden" data-testid="requests-cards">
            {rows.map((r) => (
              <MobileRow
                key={r.id}
                testid={`request-card-${r.id}`}
                title={r.item_name}
                badges={
                  <>
                    <StatusBadge value={r.status} />
                    <StatusBadge value={r.priority} />
                    {r.stock_received && (
                      <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#10B981]">
                        Stock added
                      </span>
                    )}
                  </>
                }
                fields={[
                  { label: "Quantity", value: `${r.quantity} ${r.unit}` },
                  { label: "Needed by", value: r.needed_by || "—" },
                  { label: "Vendor", value: r.vendor_name || "—" },
                  { label: "Requested by", value: r.requester_name },
                ]}
                media={
                  <div className="mt-3">
                    <ShippingBadge request={r} />
                    {(r.shipping_stage || r.tracking_number) && (
                      <ShippingTimeline request={r} testid={`shipping-timeline-mobile-${r.id}`} />
                    )}
                    {canManage && (
                      <button
                        data-testid={`shipping-edit-mobile-${r.id}`}
                        onClick={() => setShipTarget(r)}
                        className="mt-3 flex h-10 w-full items-center justify-center gap-2 border border-border text-xs font-semibold uppercase tracking-widest text-[#002FA7] active:bg-[#EEF2FF]"
                      >
                        <Truck size={15} /> Update shipping
                      </button>
                    )}
                  </div>
                }
              />
            ))}
            {canManage && (
              <div className="border-t border-border bg-slate-50 p-4">
                <div className="overline mb-2 text-slate-500">Update a status</div>
                <div className="space-y-2">
                  {rows.map((r) => (
                    <div key={r.id} className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm text-[#0F172A]">{r.item_name}</span>
                      <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                        <SelectTrigger
                          data-testid={`request-status-select-mobile-${r.id}`}
                          className="h-10 w-36 shrink-0 rounded-none border-slate-300 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="hidden overflow-x-auto border border-border bg-white md:block">
          <table className="w-full min-w-[860px] text-sm" data-testid="requests-table">
            <thead>
              <tr className="border-b border-border bg-slate-50 text-left">
                {["Item", "Qty", "Priority", "Status", "Shipping", "Needed by", "Vendor", "Requested by", canManage ? "Action" : ""].map((h) => (
                  <th key={h} className="overline px-3 py-2.5 text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                <tr data-testid={`request-row-${r.id}`} className="border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-[#0F172A]">{r.item_name}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.quantity} {r.unit}</td>
                  <td className="px-3 py-2.5"><StatusBadge value={r.priority} /></td>
                  <td className="px-3 py-2.5">
                    <StatusBadge testid={`request-status-${r.id}`} value={r.status} />
                    {r.stock_received && (
                      <span
                        data-testid={`request-received-${r.id}`}
                        className="mt-1 block text-[0.65rem] font-semibold uppercase tracking-wider text-[#10B981]"
                      >
                        Stock added
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      data-testid={`shipping-toggle-${r.id}`}
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="text-left"
                    >
                      <ShippingBadge request={r} />
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{r.needed_by || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{r.vendor_name || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{r.requester_name}</td>
                  {canManage && (
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                          <SelectTrigger data-testid={`request-status-select-${r.id}`} className="h-8 w-32 rounded-none border-slate-300 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button
                          data-testid={`shipping-edit-${r.id}`}
                          onClick={() => setShipTarget(r)}
                          title="Update shipping"
                          className="border border-border p-1.5 text-[#002FA7] transition-colors duration-150 hover:bg-[#EEF2FF]"
                        >
                          <Truck size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                {expanded === r.id && (
                  <tr className="border-b border-border bg-slate-50">
                    <td colSpan={canManage ? 9 : 8} className="px-3 pb-4">
                      <ShippingTimeline request={r} testid={`shipping-timeline-${r.id}`} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">New supply request</DialogTitle>
            <DialogDescription>Tell procurement what the floor needs and by when.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-request-form">
            <div>
              <Label className="overline text-slate-500">Item needed</Label>
              <Input data-testid="request-item-input" value={form.item_name} onChange={set("item_name")} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Quantity</Label>
                <Input data-testid="request-quantity-input" type="number" value={form.quantity} onChange={set("quantity")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Unit</Label>
                <Input data-testid="request-unit-input" value={form.unit} onChange={set("unit")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="request-priority-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Needed by</Label>
                <Input data-testid="request-needed-input" type="date" value={form.needed_by} onChange={set("needed_by")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="overline text-slate-500">Preferred vendor</Label>
              <Input data-testid="request-vendor-input" value={form.vendor_name} onChange={set("vendor_name")} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="overline text-slate-500">Notes</Label>
              <Textarea data-testid="request-notes-input" value={form.notes} onChange={set("notes")} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <DialogFooter>
              <Button data-testid="save-request-button" type="submit" className="rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A]">
                Submit request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {shipTarget && (
        <ShippingDialog
          key={shipTarget.id}
          request={shipTarget}
          open={!!shipTarget}
          onOpenChange={(v) => !v && setShipTarget(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
